import React from "react";

import * as Sentry from "@sentry/react";
import { ThemeProvider, TrackingContext } from "@shapr3d/frontend-lib";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { passthrough } from "msw";
import { CookiesProvider } from "react-cookie";
import ReactDOM from "react-dom/client";
import { Toaster } from "react-hot-toast";
import { BrowserRouter } from "react-router-dom";

import App from "./components/App/App";
import ErrorPage from "./components/ErrorPage/ErrorPage";
import { Environment, environment, SENTRY_DSN } from "./config";
import ConfigProvider from "./contexts/ConfigProvider";
import StoreProvider from "./contexts/StoreProvider";
import { trackingAdapter } from "./lib/TrackingAdapter";
import reportWebVitals from "./reportWebVitals";
import { version } from "./version";

if (import.meta.env.VITE_APP_ENV === "local") {
  const { worker } = await import("./mocks/browser");
  await worker.start({
    // This is to filter out all the analytics and telemetry stuff to avoid spamming the console
    onUnhandledRequest(req, print) {
      const url = new URL(req.url);

      if (
        url.host.includes("ingest.sentry.io") ||
        url.pathname.match(/favicon/) ||
        url.pathname.includes("/favicon.png") ||
        url.pathname.includes("/fonts") ||
        url.pathname.includes("/assets") ||
        url.pathname.includes("/node_modules") ||
        url.pathname.includes("/src") ||
        url.pathname.includes("ads/ga-audiences") ||
        url.pathname.includes("hot-update.js") ||
        url.protocol.includes("chrome-extension")
      ) {
        return;
      }

      // GTM is disabled in LoadGTMLayout for local environments
      if (url.host.includes("googletagmanager") || url.host.includes("gtm-")) {
        return passthrough();
      }

      if (url.host.includes("stage.telemetry.api.shapr3d.com")) {
        (async () => {
          const telemetryRequest = (await req.json()) as { event_name: string }[];
          console.groupCollapsed(
            `Telemetry events:`,
            `${telemetryRequest.map((e) => e.event_name).join(", ")}`
          );
          console.table(telemetryRequest, ["event_name"]);
          console.groupEnd();
        })();

        return;
      }

      print.warning();
    },
  });
}

console.info("App version:", version);

if (environment !== Environment.PROD) {
  console.info("ENV:", import.meta.env.VITE_APP_ENV);
}

if (environment !== Environment.DEV) {
  Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: false, maskAllInputs: true }),
    ],
    environment,
    // Adjust the sampling rate for performance monitoring
    // Fine tune based on the quota and data accuracy goals
    tracesSampleRate: 0.1,
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={<ErrorPage />}
      beforeCapture={(scope) => {
        scope.setTag("location", "provider");
      }}
    >
      <TrackingContext.Provider value={trackingAdapter}>
        <CookiesProvider>
          <ConfigProvider>
            <StoreProvider>
              <QueryClientProvider client={queryClient}>
                <BrowserRouter>
                  <ThemeProvider>
                    <Toaster
                      containerStyle={{ top: "88px", position: "sticky", left: 0, right: 0 }}
                      position="top-center"
                      toastOptions={{
                        icon: null,
                        style: {
                          transformOrigin: "50% 300%",
                          color: "var(--text-primary-label)",
                          background: "var(--snackbar-background)",
                          borderRadius: "var(--spacing-1x)",
                          boxShadow: "none",
                          fontSize: "12px",
                        },
                        success: {
                          style: {
                            background:
                              "color-mix(in srgb, var(--control-success-label) 10%, var(--background))",
                          },
                        },
                        error: {
                          style: {
                            background:
                              "color-mix(in srgb, var(--control-error-label) 10%, var(--background))",
                          },
                        },
                      }}
                    />
                    <App />
                  </ThemeProvider>
                </BrowserRouter>
              </QueryClientProvider>
            </StoreProvider>
          </ConfigProvider>
        </CookiesProvider>
      </TrackingContext.Provider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
